import React, { FC, useEffect, useState } from "react";
import { useSendPasswordResetEmail } from "react-firebase-hooks/auth";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import LoadingScreen from "../../components/LoadingScreen";
import SaveButton from "../../components/SaveButton";
import TextField from "../../components/styled/TextField";
import { firebaseAuth } from "../../configs/firebase";
import { DialogController } from "../../hooks/use-dialog";
import { pushError, pushSuccess } from "../../slices/notifications";
import { useDispatch } from "../../store";
import { getBaseUrl } from "../../utils/urls";

export type RequestPasswordResetParams = {
  email: string;
};

type Props = {
  dialog: DialogController<RequestPasswordResetParams>;
};

const RequestPasswordReset: FC<Props> = ({ dialog }) => {
  const [email, setEmail] = useState<string>(dialog?.data?.email || "");
  const [sendPasswordResetEmail, sending, error] =
    useSendPasswordResetEmail(firebaseAuth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (dialog.open) {
      setEmail(dialog?.data?.email || "");
    } else {
      setEmail("");
    }
  }, [dialog.open]);
  const handleRequest = async () => {
    const success = await sendPasswordResetEmail(email, {
      url: `${getBaseUrl()}/login`,
    });
    if (success) {
      dispatch(
        pushSuccess(
          "If an account it registered to " +
            email +
            " then a reset email has been sent!",
        ),
      );
    } else if (error?.message) {
      dispatch(pushError(error?.message));
    }
    dialog.handleClose();
  };

  if (sending) {
    return <LoadingScreen />;
  }

  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.handleClose}
      scroll="paper"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        Request Password Reset
        <IconButton
          color="primary"
          onClick={() => dialog.handleClose()}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container disableGutters>
          <Stack sx={{ pt: 2 }}>
            <TextField
              label="Email"
              InputLabelProps={{ shrink: true }}
              error={!email}
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
            />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dialog.handleClose()}>Cancel</Button>
        <SaveButton color="primary" onClick={handleRequest} disabled={!email}>
          Request Password Reset
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

export default RequestPasswordReset;
